import { useState, type ReactNode } from 'react';
import { passwordRuleText } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { WizardField } from './controls';
import styles from './PasswordField.module.css';

/** The four criteria of the password rule (§9.1): 12 characters, lower case, upper case, a digit. */
export function passwordScore(password: string): number {
  return [
    password.length >= 12,
    /[a-zäöüß]/.test(password),
    /[A-ZÄÖÜ]/.test(password),
    /\d/.test(password),
  ].filter(Boolean).length;
}

export interface PasswordFieldProps {
  label?: string | undefined;
  value: string;
  onChange: (value: string) => void;
  onBlur?: (() => void) | undefined;
  error?: ReactNode | undefined;
  /** `new-password` (default) or `current-password`. */
  autoComplete?: string | undefined;
  name?: string | undefined;
  /** Draws the strength meter (off for „Passwort wiederholen" and the current password). */
  meter?: boolean | undefined;
  autoFocus?: boolean | undefined;
}

/** Password input with the text toggle „anzeigen" and the strength meter (all bars green = rule met). */
export function PasswordField({
  label = 'Passwort',
  value,
  onChange,
  onBlur,
  error,
  autoComplete = 'new-password',
  name = 'password',
  meter = true,
  autoFocus,
}: PasswordFieldProps) {
  const [visible, setVisible] = useState(false);
  const score = passwordScore(value);
  const tone = score === 4 ? styles.strong : score === 3 ? styles.fair : styles.weak;
  return (
    <div className={styles.wrap}>
      <WizardField
        label={label}
        type={visible ? 'text' : 'password'}
        name={name}
        value={value}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
        error={error}
        className={clsx(!visible && value && styles.masked)}
        trailing={
          <button
            type="button"
            className={styles.toggle}
            aria-pressed={visible}
            onClick={() => setVisible((current) => !current)}
          >
            {visible ? 'verbergen' : 'anzeigen'}
          </button>
        }
      />
      {meter ? (
        <div className={styles.meter}>
          {[0, 1, 2, 3].map((index) => (
            <span
              key={index}
              className={clsx(styles.bar, value && index < score && tone)}
              aria-hidden="true"
            />
          ))}
          <span className={styles.srOnly} aria-live="polite">
            {`Passwortstärke ${value ? score : 0} von 4 · ${passwordRuleText}`}
          </span>
        </div>
      ) : null}
    </div>
  );
}
